import {
    GetColaboradoresQuery,
    useGetColaboradoresQuery,
} from "src/gql/schema"
import { useProfile } from "src/shared/hooks/useProfile"

const AREA_HOSPEDAJE = "Hospedaje"
const AREA_ALIMENTOS_Y_BEBIDAS = "Alimentos y Bebidas"

// De estas areas solo se ofrece a quien esta en turno; de cualquier otra, solo los puestos de PUESTOS.
const AREAS_CON_TURNO = [AREA_HOSPEDAJE, AREA_ALIMENTOS_Y_BEBIDAS]

// A gerencia se le ofrece siempre, este o no en turno, asi que sus puestos salen de las listas por area.
const PUESTOS_GERENCIA = ["Gerente general", "Gerente operativo", "Gerente de alimentos y bebidas"]

const PUESTOS = ["Recepcionista", "Valet parking"]
const PUESTOS_ESTANCIA = ["Recepcionista", "Valet parking"]

type TipoPersonal = "roomservice" | "estancia"

type usePersonalListOptions = {
    type?: TipoPersonal
}

type usePersonalRSParams = {
    skip?: boolean
}

type ColaboradorDeLista = GetColaboradoresQuery["colaboradores"][number]

// Ojo: el area del puesto y el area del colaborador son campos distintos de la query y pueden no coincidir.
const getAreaDelPuesto = (colaborador: ColaboradorDeLista) => colaborador?.puesto?.area?.nombre || ""
const getPuesto = (colaborador: ColaboradorDeLista) => colaborador?.puesto?.nombre || ""
const getAreaDelColaborador = (colaborador: ColaboradorDeLista) => colaborador?.area?.nombre || ""
const getPuestosEnElHotel = (colaborador: ColaboradorDeLista) =>
    colaborador?.colaborador_in_hotel?.map((hotelColaborador) => hotelColaborador?.puesto?.nombre || "") || []

// Se revisa el puesto principal y los del hotel: la gerencia puede venir por cualquiera de los dos.
const esGerente = (colaborador: ColaboradorDeLista) =>
    PUESTOS_GERENCIA.includes(getPuesto(colaborador)) ||
    getPuestosEnElHotel(colaborador).some((puesto) => PUESTOS_GERENCIA.includes(puesto))

// Base: de Hospedaje y A&B solo los que estan en turno; del resto de areas, solo ciertos puestos.
const estaDisponible = (colaborador: ColaboradorDeLista) =>
    esGerente(colaborador) ||
    (AREAS_CON_TURNO.includes(getAreaDelPuesto(colaborador))
        ? colaborador?.en_turno
        : PUESTOS.includes(getPuesto(colaborador)))

const atiendeRoomService = (colaborador: ColaboradorDeLista) =>
    esGerente(colaborador) ||
    (colaborador?.en_turno && getAreaDelColaborador(colaborador) === AREA_ALIMENTOS_Y_BEBIDAS)

const atiendeEstancia = (colaborador: ColaboradorDeLista) =>
    esGerente(colaborador) ||
    (colaborador?.en_turno &&
        (getAreaDelColaborador(colaborador) === AREA_ALIMENTOS_Y_BEBIDAS ||
            getPuestosEnElHotel(colaborador).some((puesto) => PUESTOS_ESTANCIA.includes(puesto))))

const FILTRO_POR_TIPO: Record<TipoPersonal, (colaborador: ColaboradorDeLista) => boolean> = {
    roomservice: atiendeRoomService,
    estancia: atiendeEstancia,
}

export function usePersonalList(options?: usePersonalListOptions) {
    const { hotel_id } = useProfile()

    const { data } = useGetColaboradoresQuery({
        variables: {
            hotel_id,
        },
    })

    const colaboradores = data?.colaboradores || []
    const disponibles = colaboradores.filter(estaDisponible)

    const filtroPorTipo = options?.type ? FILTRO_POR_TIPO[options.type] : null
    const delTipo = filtroPorTipo ? disponibles.filter(filtroPorTipo) : disponibles

    return {
        // Si el filtro por tipo no deja a nadie se cae a la lista base, para no dejar el input sin opciones.
        data: delTipo.length ? delTipo : disponibles,
        dataConsumoInterno: colaboradores,
    }
}

export function usePersonalRoomService(options?: usePersonalRSParams) {
    const { hotel_id } = useProfile()

    const { data, loading } = useGetColaboradoresQuery({
        variables: {
            hotel_id,
        },
        skip: options?.skip
    })

    const colaboradores = (data?.colaboradores || []).filter(
        (colaborador) => getAreaDelPuesto(colaborador) === AREA_ALIMENTOS_Y_BEBIDAS || esGerente(colaborador)
    )

    return {
        colaboradores,
        loading,
    }
}
